import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import NovedadCard from './NovedadCard';
import { useNovedadesAcciones, useNovedadesLista } from './useNovedadesWam';

export const RUTA_NOVEDADES_WAM = '/panel/novedades-wam';

/** Lista de novedades con "Nueva" en las que no se habían leído y "Cargar más". */
export const ListaNovedades = ({ lista, nuevas, onNavegar }) => {
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const novedades = (lista.data?.pages || []).flatMap((pagina) => pagina.results || []);

  if (lista.isLoading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner />
      </Box>
    );
  }
  if (lista.isError) {
    return (
      <Box py={10} textAlign="center">
        <Text color={mutedText}>No pudimos cargar las novedades.</Text>
      </Box>
    );
  }
  if (novedades.length === 0) {
    return (
      <Box py={10} textAlign="center">
        <Text color={mutedText}>Todavía no hay novedades.</Text>
      </Box>
    );
  }
  return (
    <VStack align="stretch" spacing={4}>
      {novedades.map((novedad) => (
        <NovedadCard
          key={novedad.id}
          novedad={novedad}
          esNueva={nuevas.has(novedad.id)}
          onNavegar={onNavegar}
        />
      ))}
      {lista.hasNextPage && (
        <Button variant="ghost" onClick={() => lista.fetchNextPage()} isLoading={lista.isFetchingNextPage}>
          Cargar más
        </Button>
      )}
    </VStack>
  );
};

/**
 * Abrir la lista equivale a verlas: las que estaban sin leer quedan con la
 * etiqueta "Nueva" mientras está abierta y se marcan todas como leídas.
 */
export const useMarcarAlVer = (lista, activo) => {
  const { leerTodas } = useNovedadesAcciones();
  const [nuevas, setNuevas] = useState(() => new Set());
  const procesado = useRef(false);
  const primeraPagina = lista.data?.pages?.[0];

  useEffect(() => {
    if (!activo) {
      procesado.current = false;
      return;
    }
    if (procesado.current || !primeraPagina) return;
    procesado.current = true;
    const sinLeer = (primeraPagina.results || []).filter((novedad) => !novedad.leida).map((novedad) => novedad.id);
    setNuevas(new Set(sinLeer));
    if (sinLeer.length) {
      leerTodas().catch(() => {});
    }
  }, [activo, primeraPagina, leerTodas]);

  return nuevas;
};

const NovedadesWamPanel = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const lista = useNovedadesLista({ enabled: isOpen });
  const nuevas = useMarcarAlVer(lista, isOpen);
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const verTodas = () => {
    onClose();
    navigate(RUTA_NOVEDADES_WAM);
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
          <HStack justify="space-between" pr={8}>
            <Text fontWeight="bold">Novedades de WAM</Text>
            <Button size="sm" variant="link" onClick={verTodas}>
              Ver todas
            </Button>
          </HStack>
        </DrawerHeader>
        <DrawerBody py={4}>
          <ListaNovedades lista={lista} nuevas={nuevas} onNavegar={onClose} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default NovedadesWamPanel;
